<script setup>
import { ref, computed } from 'vue'
import { Link, router, useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiTable from '@/Components/UI/UiTable.vue'
import PeriodSelector from '@/Components/UI/PeriodSelector.vue'
import LossCard from '@/Components/Cards/LossCard.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { lossCategoryConfig } from '@/composables/useRentalStatus.js'

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()

const props = defineProps({
  losses:       Array,
  summary:      Object,
  period:       { type: String, default: 'this_month' },
  period_label: { type: String, default: '' },
  period_from:  { type: String, default: '' },
  period_to:    { type: String, default: '' },
  from:         { type: String, default: null },
})

const page    = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')

// Delete confirm
const deleteTarget = ref(null)
const deleteConsequence = computed(() =>
  deleteTarget.value
    ? `This will permanently delete the ${t(`losses.categories.${deleteTarget.value.category}`)} loss of ${formatMoney(deleteTarget.value.amount_cents)}.`
    : ''
)
const deleteForm = useForm({})
const confirmDelete = () => {
  deleteForm.delete(`/losses/${deleteTarget.value.id}`, {
    onSuccess: () => { deleteTarget.value = null }
  })
}

const sourceHref = (l) => {
  if (l.rental_id)  return `/rentals/${l.rental_id}`
  if (l.vehicle_id) return `/vehicles/${l.vehicle_id}`
  return null
}
const openSource = (l) => {
  const href = sourceHref(l)
  if (href) router.visit(href)
}

const columns = computed(() => {
  const cols = [
    { key: 'date',        label: t('losses.columns.date'),     width: '9rem' },
    { key: 'category',    label: t('losses.columns.category'), width: '8rem' },
    { key: 'description', label: t('losses.columns.description'), hideBelow: 'lg' },
    { key: 'vehicle',     label: t('losses.columns.vehicle') },
    { key: 'amount',      label: t('losses.columns.amount'), numeric: true, align: 'right' },
  ]
  if (isAdmin.value) cols.push({ key: 'actions', label: '', width: '5rem', align: 'right', hideBelow: 'md' })
  return cols
})
</script>

<template>
  <PageShell
    :title="t('losses.title')"
    :back-href="from === 'dashboard' ? '/' : ''"
    :back-label="t('common.backToDashboard', 'Dashboard')"
  >
    <template #actions>
      <Link
        v-if="isAdmin"
        href="/losses/new"
      >
        <UiButton
          variant="primary"
          icon-left="plus"
        >
          {{ t('losses.new') }}
        </UiButton>
      </Link>
    </template>

    <template #filters>
      <PeriodSelector
        :current="period"
        :from="period_from"
        :to="period_to"
        class="w-full"
      />
    </template>

    <!-- Summary cards -->
    <div class="grid grid-cols-2 gap-4">
      <div
        class="rounded-xl p-4"
        :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
      >
        <p class="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
          This {{ period_label }}
        </p>
        <p
          class="text-2xl fw-value tabular-nums"
          :style="{ color: (summary?.this_period_cents ?? 0) > 0 ? 'var(--danger)' : 'var(--ink)' }"
        >
          {{ formatMoney(summary?.this_period_cents ?? 0) }}
        </p>
      </div>
      <div
        class="rounded-xl p-4"
        :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
      >
        <p class="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
          All Time
        </p>
        <p class="text-2xl fw-value tabular-nums text-[color:var(--ink)]">
          {{ formatMoney(summary?.all_time_cents ?? 0) }}
        </p>
      </div>
    </div>

    <!-- Losses table -->
    <UiTable
      :columns="columns"
      :rows="losses || []"
      :row-href="(r) => sourceHref(r)"
      responsive-cards
      @row-click="openSource"
    >
      <template #cell-date="{ row }">
        <span class="text-[color:var(--inkSoft)] whitespace-nowrap">{{ formatDate(row.occurred_on) }}</span>
      </template>
      <template #cell-category="{ row }">
        <span
          class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
          :class="[lossCategoryConfig[row.category]?.bg, lossCategoryConfig[row.category]?.text]"
        >{{ t(`losses.categories.${row.category}`) }}</span>
      </template>
      <template #cell-description="{ row }">
        <span class="text-[color:var(--inkSoft)] block max-w-xs truncate">{{ row.description }}</span>
      </template>
      <template #cell-vehicle="{ row }">
        <Link
          v-if="row.vehicle_id"
          :href="`/vehicles/${row.vehicle_id}`"
          class="text-[color:var(--accent)] hover:underline text-sm"
          @click.stop
        >
          {{ row.vehicle_name }}
        </Link>
        <span
          v-else
          class="text-[color:var(--muted)] text-sm"
          style="font-variant-numeric: normal;"
        >—</span>
      </template>
      <template #cell-amount="{ row }">
        <span class="fw-value text-[color:var(--danger)]">{{ formatMoney(row.amount_cents) }}</span>
      </template>
      <template #cell-actions="{ row }">
        <button
          type="button"
          class="text-xs text-[color:var(--danger)] hover:underline transition-colors"
          @click.stop="deleteTarget = row"
        >
          {{ t('common.delete') }}
        </button>
      </template>
      <template #card="{ row }">
        <LossCard :loss="row" />
      </template>
      <template #empty>
        <EmptyState
          type="first-use"
          :title="t('losses.title') + ' — none recorded'"
          :message="isAdmin ? 'Track unpaid rentals, damages, and other costs here.' : 'No losses recorded yet.'"
          :cta="isAdmin ? t('losses.new') : ''"
          cta-href="/losses/new"
        />
      </template>
    </UiTable>

    <!-- Delete confirm -->
    <ConfirmDialog
      :open="!!deleteTarget"
      title="Delete Loss Record"
      :consequence="deleteConsequence"
      confirm-text="Delete permanently"
      danger
      @confirm="confirmDelete"
      @cancel="deleteTarget = null"
    />
  </PageShell>
</template>
